/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import debounce from 'lodash.debounce';
import uniqBy from 'lodash.uniqby';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {ALIGNED_ROUTES_CONFIG} from '@ion/core/src/services/Here';
import {createCSV} from '@ion/core/src/services/HereUtils';
import {
  GenericObject,
  HistoryEventByAsset,
  HVSRecord
} from '@ion/core/src/types/entities';
import {deepCopy} from '@ion/core/src/utils/ObjectUtil';
import {HistoryReportTypeEnum} from '@ion/dls/src/types/types';
import useRootManager from '../../client/useRootManager';
import EventDetailPopup, {popupContentStyle} from '../components/EventDetailPopup';
import {
  ALL_FEATURES_DEF,
  EXTENT_FACTOR,
  IDENTIFIER_PROPERTY,
  IDENTIFIER_TYPE,
  LIVE_HISTORY_FIELDS,
  LIVE_HISTORY_LAST_POSITION_LAYER_ID,
  LIVE_HISTORY_LAYER_ID,
  LiveHistoryLayerProps,
  mapViewGoToOptions,
  WEBMAP_CVS_TITLE
} from '../constants';
import {
  assetIdField,
  createAssetsMap,
  createClientAssetRenderer,
  esriTypeCast,
  getHistoryByAssetRenderer,
  getIconSizeByZoom,
  getIconSizeLabelByZoom,
  getLabelClass,
  getLayerByTitle,
  getSevereEventType,
  getSortedEvents,
  hasOffsetIcon,
  IconZoomThreshold,
  layerExists,
  mergeArrayOrObject,
  onLayerClickEvent,
  RendererMapType,
  VERSION
} from '../helpers/MapViewUtils';
import useLayerRendererWithIconSize from '../hooks/useLayerRendererWithIconSize';
import {isLiveHistoryEnabled} from './LayerChooser';

type FeatureLayerProps = {
  layerId: string;
  graphics: __esri.Graphic[];
  rendererCallBack?: Function;
  addLabels?: boolean;
};

// Static Events for view by Asset
const eventValuesByAsset = Object.values(HistoryEventByAsset);

/**
 * @LiveHistoryLayer
 *
 * Defines the Live History Layer which manages rendering a layer containing trip
 * Assets with Connected lines of all trips.
 *
 *
 * ```typescript
 *
 *    import LiveHistoryLayer from '@ion/dls/src/maps/layers/LiveHistoryLayer';
 * 
 *   <LiveHistoryLayer props={...props} />
 *
 * ```
 */
const LiveHistoryLayer = ({
  map,
  view,
  layerProps : {
    assets,
    layers,
    basemap,
    isActive,
    reportType,
    hasIONVision,
    setIONVision,
    labelContent,
    assetsUpdates,
    lastPositions,
    isHistoryFetching,
    routes = {},
    routesUpdates = {}
  } = {} as any
}: LiveHistoryLayerProps) => {
  const isMounted                 = useHasMounted();
  const {render, createContainer} = useRootManager();
  const liveHistoryLayerRef       = useRef(null);
  const lastPositionLayerRef      = useRef(null);
  const viewClickRef              = useRef(null);
  const layersRef                 = useRef(layers);
  const reportTypeRef             = useRef(reportType);
  const assetColorsRef            = useRef<Record<string, string>>({});
  const iconSizeLabelRef          = useRef<string | null>(null);
  const mapZoomEventHandlerRef    = useRef<__esri.Handle | null | undefined>(null);
  const cvsLayer                  = getLayerByTitle(map?.layers as any, WEBMAP_CVS_TITLE);
  
  const {
    setLayerRendererWithIconSize,
    getLayerRendererWithIconSize
  } = useLayerRendererWithIconSize();
  
  const popupContainerNode = useMemo(() => {
    if (isMounted) {
      return createContainer('asset-history-popup');
    }
  }, [isMounted]);

  const createLayerRenderer = useCallback((layerFeatures: HVSRecord[], reportType: HistoryReportTypeEnum) => {
    // each asset's markers take the same color as its
    // line - restored from the colors kept as its events arrived, for the
    // points read back off the layer without one ...
    const features = layerFeatures?.map(feature => {
      const color = assetColorsRef.current[feature?.assetId];
      return feature?.color || !color ? feature : {...feature, color};
    });
    // Renderer for Normal icon size
    const normalIconSizeRenderer = getHistoryByAssetRenderer({
      assets   : features,
      iconSize : getIconSizeByZoom(IconZoomThreshold.Normal as number),
      reportType
    });
    // Renderer for medium icon size
    const mediumIconSizeRenderer = getHistoryByAssetRenderer({
      assets   : features,
      iconSize : getIconSizeByZoom(IconZoomThreshold.Normal + 1 as number),
      offset   : true,
      reportType
    });
    // Renderer for Large icon size
    const largeIconSizeRenderer = getHistoryByAssetRenderer({
      assets   : features,
      iconSize : getIconSizeByZoom(IconZoomThreshold.Large + 1 as number),
      offset   : true,
      reportType
    });
    
    const layerRenderers: RendererMapType = {
      Normal : normalIconSizeRenderer,
      Medium : mediumIconSizeRenderer,
      Large  : largeIconSizeRenderer
    };

    // Store Renderes into Hook
    setLayerRendererWithIconSize(layerRenderers);

    // Get the current Renderer based on the view's zoom
    const zoom               = view?.zoom;
    const iconSize           = getIconSizeByZoom(zoom as number);
    const isOffsetIcon       = hasOffsetIcon(zoom as number);
    iconSizeLabelRef.current = getIconSizeLabelByZoom(zoom as number);
    return getHistoryByAssetRenderer({assets: features, reportType, iconSize, offset: isOffsetIcon});
  }, [view]);

  const assignLayerRenderer = useCallback(debounce(zoom => {
    const renderer = getLayerRendererWithIconSize(zoom);

    if (liveHistoryLayerRef.current && renderer) {
      liveHistoryLayerRef.current.renderer = renderer;
    }
  }, 300), [getLayerRendererWithIconSize]);

  const removeLayer = useCallback(() => {
    map.removeMany([liveHistoryLayerRef.current, lastPositionLayerRef.current]);
    liveHistoryLayerRef.current = null;
    lastPositionLayerRef.current = null;
  }, []);

  const isLiveHistoryActive = useCallback((layers: string[], isSymbolLayer: boolean = false): boolean => {
    const isAssetReport = reportTypeRef.current === HistoryReportTypeEnum.Asset;
    if (isSymbolLayer && !isAssetReport) {
      return false;
    }
    return isLiveHistoryEnabled(layers);
  }, []);

  const setVisibilityToSymbolLayer = useCallback(layers => {
    if (liveHistoryLayerRef.current) {
      liveHistoryLayerRef.current.visible = isLiveHistoryActive(layers, true);
    }
  }, []);

  useEffect(() => {
    return () => {
      viewClickRef?.current?.remove('click', viewClickHandler);
      mapZoomEventHandlerRef.current?.remove();
      iconSizeLabelRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isActive) {
      removeLayer();
    }
  }, [isActive]);

  useEffect(() => {
    if (isHistoryFetching) {
      assetColorsRef.current = {};
      map.removeMany([liveHistoryLayerRef.current]);
      liveHistoryLayerRef.current = null;
    }
  }, [isHistoryFetching]);

  useEffect(() => {
    reportTypeRef.current = reportType;
    setVisibilityToSymbolLayer(layersRef.current);
    // captured once rather than re-read off the ref after each await - the
    // layer can be torn down mid-chain (the fetch this same Apply kicks off
    // flips `isHistoryFetching`, which removes it), and a stale layer is
    // never written to rather than silently failing against a null ref ...
    const layer = liveHistoryLayerRef.current;
    if (!layer) {
      return;
    }
    layer.queryFeatures().then(function({features}) {
      if (liveHistoryLayerRef.current !== layer) {
        return;
      }
      const assets = features?.map(({attributes}) => attributes);
      removeAllFeature(layer).then(() => {
        if (liveHistoryLayerRef.current !== layer) {
          return;
        }
        addFeatures(assets, true);
      });
    });
  }, [reportType]);

  useEffect(() => {
    setVisibilityToSymbolLayer(layers);
    layersRef.current = layers;
  }, [layers]);

  useEffect(() => {
    showAssets(assets, routes);
  }, []);

  useEffect(() => {
    if (assets?.length) {
      showAssets(assets, routes, true);
    }
  }, [assets, routes]);

  useEffect(() => {
    if (assetsUpdates?.length) {
      const nodes   = getNodes(assetsUpdates, routesUpdates);
      const current = assetsUpdates?.map(({messageUuid}) => messageUuid);
      const updates = nodes?.filter(({messageUuid}) => current.includes(messageUuid));
      addFeatures(updates);
    }
  }, [assetsUpdates, routesUpdates]);

  useEffect(() => {
    showLastPosition(lastPositions);
  }, [lastPositions, routes, routesUpdates]);

  useEffect(() => {
    if (lastPositionLayerRef.current) {
      const labelClass = getLabelClass({
        selectedAsset      : null,
        identifierType     : IDENTIFIER_TYPE,
        identifierProperty : IDENTIFIER_PROPERTY,
        defaultColor       : true,
        isCVSRenderer      : true,
        showAllLabels      : true,
        labelContent,
        basemap
      });
      lastPositionLayerRef.current.labelingInfo = labelClass;
      lastPositionLayerRef.current?.refresh();
    }
  }, [labelContent, basemap]);

  const shouldClearLayer = useCallback((assets: HVSRecord[], isActive: boolean): boolean => (
    !assets || assets.length === 0 || !isActive
  ), []);

  const showPopup = useCallback(({attributes}) => {
    popupContainerNode.style.cssText = popupContentStyle;
    render(<EventDetailPopup asset={attributes}
      setIONVision={setIONVision}
      hasIOnVision={hasIONVision} />, popupContainerNode);
    view.popup.open({
      location : {
        type      : 'point',
        longitude : attributes?.longitude,
        latitude  : attributes?.latitude
      },
      content : popupContainerNode
    });
  }, [popupContainerNode, view]);

  const viewClickHandler = useCallback(debounce(function({x, y}) {
    if (isLiveHistoryEnabled(layersRef.current)) {
      onLayerClickEvent({
        layerId        : [LIVE_HISTORY_LAYER_ID, LIVE_HISTORY_LAST_POSITION_LAYER_ID],
        callback       : showPopup,
        includeAny     : true,
        includeResults : true,
        hitTestVersion : VERSION.V2,
        view,
        x,
        y
      });
    }
  }, 200), [showPopup]);

  const getNodes = useCallback((assets: HVSRecord[], routes: any = []) => {
    if (assets?.length) {
      const assetsMap = createAssetsMap(assets);
      for (const key in assetsMap) {
        const {ALIGNED_ROUTES_CSV_HEADERS} = ALIGNED_ROUTES_CONFIG;
        const requestBody = createCSV(assetsMap[key], ALIGNED_ROUTES_CSV_HEADERS);
        const result      = routes[requestBody];
        // the aligned nodes are copies of the events
        // taken before the asset was given its color, so they carry the
        // color over from its events - the same color its line is drawn in ...
        const color       = assetsMap[key]?.[0]?.color;
        if (result) {
          assetsMap[key] = color
            ? {...result, nodes: result.nodes?.map(node => ({...node, color}))}
            : result;
        }
      }
      return Object.values(assetsMap).map(asset => {
        const nodes = (asset as any).nodes || asset;
        return nodes;
      }).flat();
    }
  }, []);

  const showAssets = async(assets: HVSRecord[], routes: any, initialLoad: boolean = false) => {
    if (shouldClearLayer(assets, isActive)) {
      removeLayer();
      return false;
    };

    const nodes = getNodes(assets, routes);

    if (!liveHistoryLayerRef.current) {
      const {graphics, features} = await createGraphics(nodes, LIVE_HISTORY_FIELDS);
      if (!layerExists(map, LIVE_HISTORY_LAYER_ID)) {
        liveHistoryLayerRef.current = createFeatureLayer({
          layerId          : LIVE_HISTORY_LAYER_ID,
          rendererCallBack : () => createLayerRenderer(features, reportTypeRef.current),
          graphics
        }
        );
        map.layers.push(liveHistoryLayerRef.current);
        map.layers.reorder(liveHistoryLayerRef.current, map.layers.length-2);
      }
      setVisibilityToSymbolLayer(layers);
      viewClickRef.current = view.on('click', viewClickHandler);
      mapZoomEventHandlerRef.current = view?.watch('zoom', zoom => {
        const iconSizeLabel = getIconSizeLabelByZoom(zoom as number);
        if (Number.isInteger(zoom) && iconSizeLabelRef.current !== iconSizeLabel) {
          iconSizeLabelRef.current = iconSizeLabel;
          assignLayerRenderer(zoom);
        }
      });
    } else if (initialLoad) {
      addFeatures(nodes, true);
    }
  };

  const createGraphics = async(assets: HVSRecord[], mapFields?: GenericObject[]) => {
    const graphics: Graphic[] = [];
    const severeEventType = getSevereEventType();
    const eventByAsset = mergeArrayOrObject(eventValuesByAsset, severeEventType);
    // Avoid unnecessary iteration by creating a unique asset list once
    const uniqueAssets = uniqBy(assets, assetIdField);
    uniqueAssets.forEach((asset: HVSRecord) => {
      // Get the sorted events for the asset
      const sortedEvents = deepCopy(getSortedEvents(assets, assetIdField, asset?.assetId));
      // Process each sorted event
      sortedEvents.forEach(sortedEvent => {
        const isHistoryStaticEvent = eventByAsset.includes(sortedEvent.eventType);
        if (sortedEvent.color) {
          assetColorsRef.current[sortedEvent.assetId] = sortedEvent.color;
        }
        // Set bearing and renderer field values
        if (!isHistoryStaticEvent || reportType === HistoryReportTypeEnum.Event) {
          sortedEvent.bearing = sortedEvent.heading;
          sortedEvent.rendererField = sortedEvent.assetId?.toString();
        } else {
          if (severeEventType?.includes(sortedEvent.eventType)) {
            sortedEvent.bearing = sortedEvent.heading;
          }
          sortedEvent.rendererField = sortedEvent.eventType;
        }
        // Create the graphic
        const {longitude, latitude} = sortedEvent;
        const point = new Point({longitude, latitude, spatialReference: {wkid: 4326}});
        const graphic = new Graphic({
          geometry   : point,
          symbol     : null,
          attributes : esriTypeCast(sortedEvent, mapFields)
        });
        graphics.push(graphic);
      });
    });
    // Extract features from graphics
    const features = graphics.map(({attributes}) => attributes);
    return {graphics, features};
  };

  const addFeatures = useCallback(async(assets: HVSRecord[], updateRenderer: boolean = false) => {
    // captured once rather than re-read off the ref after each await - the
    // layer can be torn down mid-call by a fetch running around the same
    // time flipping `isHistoryFetching`, which removes it - and a stale
    // layer is never written to rather than silently failing against a
    // null ref ...
    const layer = liveHistoryLayerRef.current;
    if (!layer) {
      return;
    }
    const {graphics, features} = await createGraphics(assets, LIVE_HISTORY_FIELDS);
    if (liveHistoryLayerRef.current !== layer) {
      return;
    }
    if (updateRenderer) {
      const {features: currentFeatures} = await layer.queryFeatures(ALL_FEATURES_DEF);
      if (liveHistoryLayerRef.current !== layer) {
        return;
      }
      const currentAttributes = currentFeatures?.map(feature => feature?.attributes) || [];
      layer.renderer =
        createLayerRenderer(mergeArrayOrObject(currentAttributes, features) as any, reportTypeRef.current);
    }
    applyEditsToLayer(layer, {addFeatures: graphics});
  }, [LIVE_HISTORY_FIELDS, lastPositions]);

  const removeAllFeature = useCallback(async(featureLayer: __esri.FeatureLayer) => {
    return featureLayer?.queryFeatures(ALL_FEATURES_DEF as any)
      ?.then(function({features}) {
        applyEditsToLayer(featureLayer, {deleteFeatures: features});
      });
  }, [ALL_FEATURES_DEF]);

  const applyEditsToLayer = useCallback((featureLayer: __esri.FeatureLayer, edits: any, callback?: Function) => {
    featureLayer?.applyEdits(edits)
      .then(() => {
        callback?.();
      });
  }, []);

  const zoomToFeatures = useCallback(() => {
    liveHistoryLayerRef.current?.queryExtent()
      .then(({count, extent}) => {
        count && view?.goTo({geometry: extent.expand(EXTENT_FACTOR)}, mapViewGoToOptions);
      });
  }, [EXTENT_FACTOR]);

  const showLastPosition = async(assets: HVSRecord[]) => {
    if (shouldClearLayer(assets, isActive)) {
      map.remove(lastPositionLayerRef.current);
      lastPositionLayerRef.current = null;
      return false;
    };

    if (!lastPositionLayerRef.current) {
      const {graphics} = await createGraphics(assets, LIVE_HISTORY_FIELDS);
      if (!layerExists(map, LIVE_HISTORY_LAST_POSITION_LAYER_ID)) {
        lastPositionLayerRef.current = createFeatureLayer({
          layerId          : LIVE_HISTORY_LAST_POSITION_LAYER_ID,
          rendererCallBack : () => cvsLayer?.renderer || createClientAssetRenderer(),
          addLabels        : true,
          graphics
        });
        map.layers.push(lastPositionLayerRef.current);
      } else {
        lastPositionLayerRef.current = map.findLayerById(LIVE_HISTORY_LAST_POSITION_LAYER_ID);
      }
      viewClickRef.current = view.on('click', viewClickHandler);
    } else {
      addLastPositionFeatures(assets);
    }
  };

  const addLastPositionFeatures = useCallback(async(assets: HVSRecord[]) => {
    if (lastPositionLayerRef.current) {
      const {graphics} = await createGraphics(assets, LIVE_HISTORY_FIELDS);
      lastPositionLayerRef.current?.queryFeatures(ALL_FEATURES_DEF).then(({features}) => {
        applyEditsToLayer(
          lastPositionLayerRef.current,
          {
            deleteFeatures : features,
            addFeatures    : graphics
          },
          () => setTimeout(() => lastPositionLayerRef.current?.refresh(), 200)
        );
      });
    }
  }, [applyEditsToLayer, ALL_FEATURES_DEF, LIVE_HISTORY_FIELDS]);

  const createFeatureLayer = useCallback(({
    layerId,
    graphics,
    rendererCallBack,
    addLabels
  }: FeatureLayerProps) => {
    return new FeatureLayer({
      title            : layerId,
      id               : layerId,
      source           : graphics,
      geometryType     : 'point' as const,
      spatialReference : {wkid: 4326},
      objectIdField    : 'OBJECTID',
      outFields        : ['*'],
      fields           : LIVE_HISTORY_FIELDS as any,
      renderer         : rendererCallBack(),
      labelingInfo     : addLabels ? getLabelClass({
        selectedAsset      : null,
        identifierType     : IDENTIFIER_TYPE,
        identifierProperty : IDENTIFIER_PROPERTY,
        defaultColor       : true,
        isCVSRenderer      : true,
        showAllLabels      : true,
        labelContent,
        basemap
      }) as any : null
    });
  }, [map]);

  return null;
};

export default LiveHistoryLayer;
